import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DollarSign, TrendingUp, Wallet, Users, Plus, Pencil, Trash2, Check, X, Clock,
  ChevronRight, ShoppingBag, FileCheck, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, CartesianGrid, Legend, Area, AreaChart
} from 'recharts';

const fmtFCFA = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' FCFA';
const fmtCompact = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, '') + ' Md';
  if (v >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + ' M';
  if (v >= 1e3) return (n / 1e3).toFixed(0) + ' k';
  return String(n || 0);
};

export default function Revenus({ user, onLogout, embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [charges, setCharges] = useState([]);
  const [chargesTotal, setChargesTotal] = useState(0);
  const [chargesLastUpdate, setChargesLastUpdate] = useState('');
  const [showCharges, setShowCharges] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDesign, setEditDesign] = useState('');
  const [editMontant, setEditMontant] = useState('');
  const [adding, setAdding] = useState(false);
  const [newDesign, setNewDesign] = useState('');
  const [newMontant, setNewMontant] = useState('');

  useEffect(() => {
    loadAll();
    // Refetch quand l'utilisateur revient sur l'onglet (garantit que les
    // Revenus reflètent immédiatement les contrats signés ou supprimés).
    const onFocus = () => loadAll();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') loadAll();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
    };
    /* eslint-disable-next-line */
  }, []);

  const loadAll = async () => {
    try {
      const [dashRes, chargesRes] = await Promise.all([
        axios.get('/revenus-module/dashboard'),
        axios.get('/revenus-module/charges'),
      ]);
      setData(dashRes.data);
      setCharges(chargesRes.data.items);
      setChargesTotal(chargesRes.data.total);
      setChargesLastUpdate(chargesRes.data.last_updated);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement des revenus');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditDesign(c.designation);
    setEditMontant(String(c.montant || 0));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDesign('');
    setEditMontant('');
  };
  const saveEdit = async (id) => {
    try {
      await axios.put(`/revenus-module/charges/${id}`, {
        designation: editDesign,
        montant: parseFloat(editMontant) || 0,
      });
      toast.success('Charge mise à jour');
      cancelEdit();
      await loadAll();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  const deleteCharge = async (id) => {
    if (!window.confirm('Supprimer cette charge ?')) return;
    try {
      await axios.delete(`/revenus-module/charges/${id}`);
      toast.success('Charge supprimée');
      await loadAll();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };
  const addCharge = async () => {
    if (!newDesign.trim()) {
      toast.error('Désignation requise');
      return;
    }
    try {
      await axios.post('/revenus-module/charges', {
        designation: newDesign,
        montant: parseFloat(newMontant) || 0,
      });
      toast.success('Charge ajoutée');
      setNewDesign('');
      setNewMontant('');
      setAdding(false);
      await loadAll();
    } catch (e) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  if (loading) {
    const spinner = (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-emerald-500"></div>
      </div>
    );
    if (embedded) return spinner;
    return (
      <Layout user={user} onLogout={onLogout} currentPage="revenus">
        {spinner}
      </Layout>
    );
  }

  const kpis = data?.kpis || {};
  const cards = [
    {
      key: 'revenus_totaux',
      title: 'Revenus totaux',
      subtitle: 'Selon les contrats signés',
      icon: ShoppingBag,
      accent: 'emerald',
      values: kpis.revenus_totaux,
      chartColor: '#10b981',
    },
    {
      key: 'charges_mensuelles',
      title: 'Charges mensuelles',
      subtitle: 'Selon les dépenses mensuelles prévues',
      icon: FileCheck,
      accent: 'sky',
      values: kpis.charges_mensuelles,
      chartColor: '#0ea5e9',
    },
    {
      key: 'benefice',
      title: 'Bénéfice',
      subtitle: 'Revenus totaux − Charges mensuelles',
      icon: TrendingUp,
      accent: 'violet',
      values: kpis.benefice,
      chartColor: '#8b5cf6',
    },
    {
      key: 'revenus_associes',
      title: 'Revenus associés',
      subtitle: '20 % du bénéfice',
      icon: Users,
      accent: 'orange',
      values: kpis.revenus_associes,
      chartColor: '#f97316',
    },
    {
      key: 'revenus_investisseurs',
      title: 'Revenus investisseurs',
      subtitle: '20 % du bénéfice',
      icon: Users,
      accent: 'indigo',
      values: kpis.revenus_investisseurs,
      chartColor: '#6366f1',
    },
    {
      key: 'revenus_sesam',
      title: 'Revenus SESAM',
      subtitle: '60 % du bénéfice (part société)',
      icon: TrendingUp,
      accent: 'blue',
      values: kpis.revenus_sesam,
      chartColor: '#2563eb',
    },
  ];

  const accentStyles = {
    emerald: {
      cardBg: 'bg-emerald-50 border-emerald-200',
      iconBg: 'bg-emerald-100 text-emerald-600',
      badge: 'text-emerald-700',
      btn: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    },
    sky: {
      cardBg: 'bg-sky-50 border-sky-200',
      iconBg: 'bg-sky-100 text-sky-600',
      badge: 'text-sky-700',
      btn: 'bg-sky-600 hover:bg-sky-700 text-white',
    },
    violet: {
      cardBg: 'bg-violet-50 border-violet-200',
      iconBg: 'bg-violet-100 text-violet-600',
      badge: 'text-violet-700',
      btn: 'bg-violet-600 hover:bg-violet-700 text-white',
    },
    orange: {
      cardBg: 'bg-orange-50 border-orange-200',
      iconBg: 'bg-orange-100 text-orange-600',
      badge: 'text-orange-700',
      btn: 'bg-orange-600 hover:bg-orange-700 text-white',
    },
    indigo: {
      cardBg: 'bg-indigo-50 border-indigo-200',
      iconBg: 'bg-indigo-100 text-indigo-600',
      badge: 'text-indigo-700',
      btn: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    },
    blue: {
      cardBg: 'bg-blue-50 border-blue-200',
      iconBg: 'bg-blue-100 text-blue-600',
      badge: 'text-blue-700',
      btn: 'bg-blue-600 hover:bg-blue-700 text-white',
    },
  };

  // Génère une sparkline à partir de l'évolution mensuelle
  const sparkData = (metric) => {
    if (!data?.evolution) return [];
    const key = metric === 'revenus_totaux' ? 'revenus'
      : metric === 'charges_mensuelles' ? 'charges'
      : 'benefice';
    const factor = (metric === 'revenus_associes' || metric === 'revenus_investisseurs') ? 0.2
      : metric === 'revenus_sesam' ? 0.6
      : 1;
    return data.evolution.map((e) => ({ v: (e[key] || 0) * factor }));
  };

  const evolutionData = data?.evolution || [];
  const comparatif = data?.comparatif;
  const comparatifBar = comparatif ? [
    { name: comparatif.mois, revenus_totaux: comparatif.revenus_totaux, charges_mensuelles: comparatif.charges_mensuelles, benefice: comparatif.benefice, revenus_associes: comparatif.revenus_associes, revenus_investisseurs: comparatif.revenus_investisseurs }
  ] : [];

  const lastUpdateFmt = (() => {
    if (!chargesLastUpdate) return '—';
    try {
      const d = new Date(chargesLastUpdate);
      return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return chargesLastUpdate; }
  })();

  const body = (
    <div className="space-y-6" data-testid="revenus-module-root">
      {/* Header + bouton Rafraîchir */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {!embedded && (
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <DollarSign className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Module Revenus</h1>
              <p className="text-gray-600 text-sm md:text-base">
                Suivez, analysez et optimisez vos performances financières
              </p>
            </div>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setLoading(true); loadAll(); }}
          data-testid="btn-refresh-revenus"
          className="ml-auto gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          title="Recalculer immédiatement les revenus à partir des contrats actuels"
        >
          <RefreshCw className="h-4 w-4" />
          Rafraîchir
        </Button>
      </div>

      {/* Bannière : total brut des contrats (somme réelle, sans proration) */}
      {data?.revenus_bruts_total > 0 && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-3"
          data-testid="revenus-bruts-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <DollarSign className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-800">Revenu mensuel des contrats actifs</div>
              <div className="text-2xl font-bold text-emerald-900">{fmtFCFA(data.revenus_bruts_total)}</div>
            </div>
          </div>
          <div className="text-right text-xs text-emerald-700 leading-tight">
            <div><b>{data.nombre_contrats}</b> contrat{data.nombre_contrats > 1 ? 's' : ''} actif{data.nombre_contrats > 1 ? 's' : ''}</div>
            <div className="italic">(somme mensuelle brute)</div>
          </div>
        </div>
      )}

        {/* 4 KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5">
          {cards.map((c) => {
            const s = accentStyles[c.accent];
            const Icon = c.icon;
            const spark = sparkData(c.key);
            const isCharges = c.key === 'charges_mensuelles';
            const handleCardClick = () => {
              if (!isCharges) return;
              setShowCharges((v) => {
                const next = !v;
                if (next) {
                  setTimeout(() => {
                    document.querySelector('[data-testid="charges-panel"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }
                return next;
              });
            };
            return (
              <Card
                key={c.key}
                onClick={handleCardClick}
                className={`border-2 ${s.cardBg} shadow-sm hover:shadow-md transition-shadow ${isCharges ? 'cursor-pointer ring-1 ring-sky-200 hover:ring-sky-400' : ''}`}
                data-testid={`kpi-card-${c.key}`}
              >
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className={`w-11 h-11 rounded-xl ${s.iconBg} flex items-center justify-center`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="w-24 h-10">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={spark} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id={`grad-${c.key}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={c.chartColor} stopOpacity={0.4} />
                              <stop offset="100%" stopColor={c.chartColor} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area type="monotone" dataKey="v" stroke={c.chartColor} strokeWidth={2} fill={`url(#grad-${c.key})`} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-gray-700">{c.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{c.subtitle}</div>
                    <div className="text-2xl font-bold text-gray-900 mt-2" data-testid={`kpi-value-${c.key}`}>
                      {fmtFCFA(c.values?.mensuel || 0)}
                    </div>
                  </div>

                  <div className="border-t border-white pt-3">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-2">Période</div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mensuel</span>
                        <span className={`font-semibold ${s.badge}`}>{fmtFCFA(c.values?.mensuel || 0)}</span>
                      </div>
                    </div>
                  </div>

                  {c.key === 'charges_mensuelles' && (
                    <Button
                      className={`w-full ${s.btn}`}
                      onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
                      data-testid="btn-toggle-charges"
                    >
                      {showCharges ? 'Masquer les détails' : 'Modifier / Ajouter des charges'} <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* Évolution mensuelle */}
          <Card className="border-2 border-gray-200 shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Évolution mensuelle (FCFA)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={evolutionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                  <Tooltip formatter={(v) => fmtFCFA(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="revenus" name="Revenus totaux" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="charges" name="Charges mensuelles" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="benefice" name="Bénéfice" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Comparatif du mois */}
          <Card className="border-2 border-gray-200 shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Comparatif des indicateurs (FCFA)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={comparatifBar}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                  <Tooltip formatter={(v) => fmtFCFA(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenus_totaux" name="Revenus totaux" fill="#10b981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="charges_mensuelles" name="Charges mensuelles" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="benefice" name="Bénéfice" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="revenus_associes" name="Revenus associés" fill="#f97316" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="revenus_investisseurs" name="Revenus investisseurs" fill="#6366f1" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Détails des charges mensuelles */}
        {showCharges && (
          <Card className="border-2 border-gray-200 shadow-sm" data-testid="charges-panel">
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Détails des charges mensuelles</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Ajoutez, modifiez vos charges. Le total se calcule automatiquement.
                  </p>
                </div>
                <button
                  onClick={() => setShowCharges(false)}
                  className="text-gray-400 hover:text-gray-600 self-start"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Total + last update */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-sky-50 border border-sky-200 rounded-xl p-4 mb-5">
                <div>
                  <div className="text-xs uppercase tracking-wide text-sky-700 font-semibold">Total des charges mensuelles</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1" data-testid="charges-total">{fmtFCFA(chargesTotal)}</div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Clock className="h-4 w-4" />
                  Dernière mise à jour : {lastUpdateFmt}
                </div>
              </div>

              <div className="flex justify-end mb-3">
                <Button
                  onClick={() => setAdding(true)}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="btn-add-charge"
                >
                  <Plus className="h-4 w-4 mr-1" /> Ajouter une charge
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-y border-gray-200">
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Désignation</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-700 w-56">Montant (FCFA)</th>
                      <th className="text-center px-4 py-3 font-semibold text-gray-700 w-40">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adding && (
                      <tr className="bg-emerald-50 border-b border-gray-200" data-testid="charge-row-new">
                        <td className="px-4 py-2">
                          <Input
                            value={newDesign}
                            onChange={(e) => setNewDesign(e.target.value)}
                            placeholder="Nouvelle désignation"
                            data-testid="input-new-design"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <Input
                            type="number"
                            value={newMontant}
                            onChange={(e) => setNewMontant(e.target.value)}
                            placeholder="0"
                            className="text-right"
                            data-testid="input-new-montant"
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <div className="flex justify-center gap-2">
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={addCharge} data-testid="btn-confirm-add">
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setAdding(false); setNewDesign(''); setNewMontant(''); }}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {charges.map((c) => (
                      <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`charge-row-${c.id}`}>
                        <td className="px-4 py-3 text-gray-800">
                          {editingId === c.id ? (
                            <Input value={editDesign} onChange={(e) => setEditDesign(e.target.value)} />
                          ) : c.designation}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900 font-medium">
                          {editingId === c.id ? (
                            <Input type="number" className="text-right" value={editMontant} onChange={(e) => setEditMontant(e.target.value)} />
                          ) : new Intl.NumberFormat('fr-FR').format(c.montant || 0)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-2">
                            {editingId === c.id ? (
                              <>
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveEdit(c.id)} data-testid={`btn-save-${c.id}`}>
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEdit}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <button className="p-1.5 text-sky-600 hover:bg-sky-100 rounded" onClick={() => startEdit(c)} data-testid={`btn-edit-${c.id}`}>
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button className="p-1.5 text-red-600 hover:bg-red-100 rounded" onClick={() => deleteCharge(c.id)} data-testid={`btn-delete-${c.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {charges.length === 0 && !adding && (
                      <tr>
                        <td colSpan="3" className="text-center py-8 text-gray-500">
                          Aucune charge. Cliquez sur « Ajouter une charge ».
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-300 font-bold">
                      <td className="px-4 py-3 text-gray-800">Total</td>
                      <td className="px-4 py-3 text-right text-lg text-sky-700">{fmtFCFA(chargesTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );

  if (embedded) return body;

  return (
    <Layout user={user} onLogout={onLogout} currentPage="revenus">
      {body}
    </Layout>
  );
}

